import { memo } from "react";
import { MessageCircle } from "lucide-react";
import type { PMIssue } from "@/domains/pm/types";
import type { OrphanedParentRef } from "@/domains/pm/utils/groupAndSort";
import { DocChip, DocChipCollapsed } from "@/components/chat/SidebarChat/DocLink/DocChip";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { useOpenLinkedDoc } from "@/domains/pm/hooks/useOpenLinkedDoc";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { IssueContextMenu } from "./IssueContextMenu";
import { ChecklistProgressBadge } from "./ChecklistProgressBadge";
import { StatusCell, DisciplineCell, AssigneeCell, DueDateCell } from "./IssueRowCellEditors";
import {
  AssigneeAvatar,
  FittedDisciplineBadges,
  StatusBadge,
} from "@/domains/pm/components/shared";
import { DraftIssueMarker, DraftIssueTitle } from "./IssueDraftViewParts";
import { useDraftIssueLabel } from "./issueDraftView";

interface IssueRowProps {
  issue: PMIssue;
  isSelected: boolean;
  onSelect: (issue: PMIssue) => void;
  buildingShortCode?: string;
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  /** Checked state of the row's bulk-action checkbox (WHA-3334). */
  isMultiSelected: boolean;
  /** WHA-3456: `shiftKey` selects the range from the anchor row. */
  onMultiSelectToggle: (issueId: number, shiftKey: boolean) => void;
  /** Full bulk-select set — lets the right-click menu act on a multi-selection (WHA-3428). */
  selectedIssueIds: ReadonlySet<number>;
  /** WHA-3331: indentation depth — 0 for top-level, 1 for subissue rendered
   *  directly under its parent. */
  indentLevel?: number;
  /** WHA-3331: when set, the row renders a "Subissue of #N — Title" chip
   *  instead of being indented (the parent was excluded by the filter). */
  orphanedParentRef?: OrphanedParentRef;
  /** Called when the user clicks the orphan-parent chip. */
  onJumpToIssue?: (id: number) => void;
}

/**
 * Memoised: a list re-renders on every selection and every reload, and a row
 * whose issue, selection state and handlers are unchanged has nothing new to
 * draw. The list hands it stable handlers so the comparison can succeed.
 */
function IssueTitleCell(
  props: Pick<
    IssueRowProps,
    "issue" | "buildingShortCode" | "indentLevel" | "orphanedParentRef" | "onJumpToIssue"
  >
) {
  const { issue, buildingShortCode, indentLevel = 0, orphanedParentRef, onJumpToIssue } = props;
  const { t } = useLanguage();
  const prefix = buildingShortCode
    ? `#${buildingShortCode}-${issue.issue_number}`
    : `#${issue.issue_number}`;
  const isSubissueRow = indentLevel > 0;
  return (
    <td
      className="min-w-[280px] px-3 py-2.5"
      style={isSubissueRow ? { paddingLeft: `${12 + 24 * indentLevel}px` } : undefined}
    >
      <div className="flex items-start gap-2">
        {isSubissueRow && (
          <span
            aria-hidden
            className="mt-0.5 shrink-0 select-none text-[11px] font-medium text-muted-foreground"
          >
            └─
          </span>
        )}
        <span className="mt-0.5 shrink-0 text-[11px] font-medium text-muted-foreground">
          {prefix}
        </span>
        <span className="text-sm leading-snug">{issue.title}</span>
        <ChecklistProgressBadge issue={issue} className="mt-0.5" />
        {orphanedParentRef && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onJumpToIssue?.(orphanedParentRef.id);
            }}
            className="ml-1 inline-flex shrink-0 items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted-foreground/10 hover:text-foreground"
            title={t.pm.issues.detail.subissueOf
              .replace("{n}", String(orphanedParentRef.issue_number))
              .replace("{title}", orphanedParentRef.title)}
          >
            ↑ #{orphanedParentRef.issue_number}
          </button>
        )}
      </div>
    </td>
  );
}

function IssueCommentsCell({ issue }: { issue: PMIssue }) {
  return (
    <td className="px-3 py-2.5 text-center">
      {(issue.comments_count ?? 0) > 0 ? (
        <span className="relative inline-flex items-center">
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <MessageCircle className="h-3.5 w-3.5" />
            {issue.comments_count}
          </span>
          {(issue.unread_mention_count ?? 0) > 0 && (
            <span
              aria-label="Unread mention"
              className="absolute -right-1.5 -top-0.5 h-2 w-2 rounded-full bg-red-500"
            />
          )}
        </span>
      ) : (
        <span className="text-[11px] text-muted-foreground">—</span>
      )}
    </td>
  );
}

function IssueDocumentsCell({ issue, openViewer }: Pick<IssueRowProps, "issue" | "openViewer">) {
  const {
    canOpen,
    nonOpenableFilenames,
    open: openDoc,
  } = useOpenLinkedDoc(issue.building_id, openViewer);
  return (
    <td className="max-w-[200px] px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
      {issue.linked_documents && issue.linked_documents.length > 0 ? (
        issue.linked_documents.length === 1 ? (
          <DocChip
            filename={issue.linked_documents[0].filename}
            onClick={
              canOpen(issue.linked_documents[0])
                ? () => openDoc(issue.linked_documents[0])
                : undefined
            }
          />
        ) : (
          <DocChipCollapsed
            filenames={issue.linked_documents.map((d) => d.filename)}
            nonOpenableFilenames={nonOpenableFilenames(issue.linked_documents)}
            onDocClick={(filename) => {
              const doc = issue.linked_documents?.find((d) => d.filename === filename);
              if (doc) openDoc(doc);
            }}
          />
        )
      ) : (
        <span className="text-[11px] text-muted-foreground">—</span>
      )}
    </td>
  );
}

export const IssueRow = memo(function IssueRow(props: IssueRowProps) {
  const {
    issue,
    isSelected,
    onSelect,
    buildingShortCode,
    openViewer,
    isMultiSelected,
    onMultiSelectToggle,
    selectedIssueIds,
    indentLevel = 0,
    orphanedParentRef,
    onJumpToIssue,
  } = props;
  return (
    <IssueContextMenu issue={issue} selectedIssueIds={selectedIssueIds}>
      <tr
        className={cn(
          "cursor-pointer border-b transition-colors",
          isSelected ? "bg-primary/5" : "hover:bg-[#f7f7f7]"
        )}
        // The second click of a double-click would close the issue the first
        // one just opened (a click on the open issue closes it).
        onClick={(e) => {
          if (e.detail > 1) return;
          onSelect(issue);
        }}
      >
        {/* Checkbox */}
        {/*
          WHA-3494: the whole cell is the hit target, not just the 14px box,
          so the multi-select checkbox is more forgiving to click.
          WHA-3456: read Shift off the click so a range can be selected.
        */}
        <td
          className="w-8 cursor-pointer px-3 py-2.5"
          onClick={(e) => {
            e.stopPropagation();
            onMultiSelectToggle(issue.id, e.shiftKey);
          }}
        >
          <input
            type="checkbox"
            className="pointer-events-none h-3.5 w-3.5 rounded border-gray-300 accent-primary"
            checked={isMultiSelected}
            // The cell's click handler owns the toggle; the input is read-only.
            readOnly
            aria-label={`Select issue #${issue.issue_number}`}
          />
        </td>

        <IssueTitleCell
          issue={issue}
          buildingShortCode={buildingShortCode}
          indentLevel={indentLevel}
          orphanedParentRef={orphanedParentRef}
          onJumpToIssue={onJumpToIssue}
        />

        {/* Status — WHA-3446: click to edit inline */}
        <td className="whitespace-nowrap px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          <StatusCell issue={issue} />
        </td>

        {/* Disciplines — WHA-3446: click to edit inline */}
        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          <DisciplineCell issue={issue} fitLabels />
        </td>

        {/* Assignees — WHA-3446: click to edit inline */}
        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          <AssigneeCell issue={issue} />
        </td>

        {/* Due Date — WHA-3446: click to edit inline */}
        <td className="whitespace-nowrap px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          <DueDateCell issue={issue} />
        </td>

        <IssueCommentsCell issue={issue} />
        <IssueDocumentsCell issue={issue} openViewer={openViewer} />
      </tr>
    </IssueContextMenu>
  );
});

function formatDueDate(dateStr: string): string {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${d.getFullYear()}`;
}

const EMPTY_CELL = <span className="text-[11px] text-muted-foreground">—</span>;

interface DraftIssueRowProps {
  /** The draft's projection (`isDraftIssueId(issue.id)`). */
  issue: PMIssue;
  /** Open in the draft panel. */
  isActive: boolean;
  /** Its save is running. */
  isSaving: boolean;
  onSelect: (draftId: number) => void;
}

/**
 * A new issue still being written, drawn with the same columns as an issue
 * row but read-only: no checkbox, no right-click menu, no inline editors —
 * every one of those writes against an id the draft does not have yet. Its
 * fields are edited in the draft panel, which a click (or Enter on its title)
 * opens.
 */
export function DraftIssueRow({ issue, isActive, isSaving, onSelect }: DraftIssueRowProps) {
  const label = useDraftIssueLabel();
  const assignees = issue.assignees ?? [];
  const disciplines = issue.disciplines ?? [];
  return (
    <tr
      data-draft-issue={issue.id}
      aria-busy={isSaving || undefined}
      className={cn(
        "border-b transition-colors",
        isSaving ? "cursor-default opacity-70" : "cursor-pointer",
        isActive ? "bg-primary/5" : !isSaving && "hover:bg-[#f7f7f7]"
      )}
      onClick={(e) => {
        if (e.detail > 1) return;
        onSelect(issue.id);
      }}
    >
      {/* Where the checkbox goes: a draft cannot be bulk-selected. */}
      <td className="w-8 px-3 py-2.5" />
      <td className="min-w-[280px] px-3 py-2.5">
        {/* A button so the row can be reached and opened from the keyboard;
            its click bubbles to the row's. */}
        <button
          type="button"
          aria-label={label(issue)}
          disabled={isSaving}
          className="flex items-start gap-2 rounded text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <DraftIssueMarker saving={isSaving} className="mt-0.5" />
          <DraftIssueTitle issue={issue} className="text-sm leading-snug" />
          <ChecklistProgressBadge issue={issue} className="mt-0.5" />
        </button>
      </td>
      <td className="whitespace-nowrap px-3 py-2.5">
        <StatusBadge status={issue.status} buildingProjectId={issue.building_project_id} />
      </td>
      <td className="px-3 py-2.5">
        {disciplines.length > 0 ? (
          <FittedDisciplineBadges disciplines={disciplines} size="sm" variant="outline" />
        ) : (
          EMPTY_CELL
        )}
      </td>
      <td className="px-3 py-2.5">
        {assignees.length > 0 ? (
          <span className="flex -space-x-1.5">
            {assignees.slice(0, 3).map((u) => (
              <AssigneeAvatar key={u.id} user={u} size={22} className="ring-1 ring-background" />
            ))}
            {assignees.length > 3 && (
              <span className="ml-1 inline-flex h-[22px] items-center px-1 text-[10px] font-medium text-muted-foreground">
                +{assignees.length - 3}
              </span>
            )}
          </span>
        ) : (
          EMPTY_CELL
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2.5">
        {issue.due_date ? (
          <span className="text-[11px] text-muted-foreground">{formatDueDate(issue.due_date)}</span>
        ) : (
          EMPTY_CELL
        )}
      </td>
      <td className="px-3 py-2.5 text-center">{EMPTY_CELL}</td>
      <td className="max-w-[200px] px-3 py-2.5">{EMPTY_CELL}</td>
    </tr>
  );
}
